import { useOrdersInfinite, useUserStatistics, type OrderSummary } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgBottomNav } from "../components/TgBottomNav";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth } from "../lib/auth";
import { miniDisplayCurrency, miniDisplayLocale } from "../lib/locale-currency";

// ─── Tab config ────────────────────────────────────────────────────────────────
type ShipTab = "ALL" | "WAIT_PICKING" | "SHIPPED" | "WAIT_CONFIRM" | "COMPLETED";

const TABS: { key: ShipTab; i18nKey: string; emoji: string }[] = [
  { key: "ALL", i18nKey: "all", emoji: "" },
  { key: "WAIT_PICKING", i18nKey: "pending", emoji: "📦" },
  { key: "SHIPPED", i18nKey: "transit", emoji: "✈" },
  { key: "WAIT_CONFIRM", i18nKey: "delivering", emoji: "🚚" },
  { key: "COMPLETED", i18nKey: "delivered", emoji: "✓" },
];

// ─── Status visual config ──────────────────────────────────────────────────────
const STATUS_STYLE: Record<
  string,
  { bg: string; fg: string; dot: string; borderColor: string } | undefined
> = {
  WAIT_PICKING: {
    bg: "var(--color-warning-soft)",
    fg: "var(--color-warning-foreground)",
    dot: "var(--color-warning)",
    borderColor: "var(--color-border)",
  },
  SHIPPED: {
    bg: "var(--color-info-soft)",
    fg: "var(--color-info)",
    dot: "var(--color-info)",
    borderColor: "var(--color-info)",
  },
  WAIT_CONFIRM: {
    bg: "var(--color-primary-soft)",
    fg: "var(--color-primary-fill)",
    dot: "var(--color-primary)",
    borderColor: "var(--color-border)",
  },
  COMPLETED: {
    bg: "var(--color-surface)",
    fg: "var(--color-text-muted)",
    dot: "var(--color-border-strong)",
    borderColor: "var(--color-border)",
  },
};

const DEFAULT_STYLE = {
  bg: "var(--color-warning-soft)",
  fg: "var(--color-warning-foreground)",
  dot: "var(--color-warning)",
  borderColor: "var(--color-border)",
};

// ─── Progress bar (5-step) for WAIT_PICKING ────────────────────────────────────
// Step 1 = active (paid+packing), steps 2-5 = future
function ShipmentProgressBar() {
  const activeStep = 1;
  const totalSteps = 5;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        marginTop: 10,
        paddingTop: 10,
        borderTop: "1px dashed var(--color-border)",
      }}
    >
      {Array.from({ length: totalSteps }, (_, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === activeStep;
        const isDone = stepNum < activeStep;
        return (
          <div key={stepNum} style={{ display: "contents" }}>
            <div
              style={{
                width: isActive ? 18 : 14,
                height: isActive ? 18 : 14,
                borderRadius: "50%",
                background: isDone
                  ? "var(--color-primary-fill)"
                  : isActive
                    ? "var(--color-primary-soft)"
                    : "var(--color-bg)",
                border: isDone
                  ? "2px solid var(--color-primary-fill)"
                  : isActive
                    ? "2px solid var(--color-primary-fill)"
                    : "2px solid var(--color-border)",
                color: isDone ? "#fff" : isActive ? "var(--color-primary-fill)" : "var(--color-text-muted)",
                display: "grid",
                placeItems: "center",
                fontSize: 9,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {isDone ? "✓" : stepNum}
            </div>
            {stepNum < totalSteps && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: isDone ? "var(--color-primary-fill)" : "var(--color-border)",
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Track summary bar for SHIPPED ────────────────────────────────────────────
function TrackSummaryBar({ latestEvent }: { latestEvent?: string | null }) {
  if (!latestEvent) return null;
  return (
    <div
      style={{
        marginTop: 10,
        padding: "6px 10px",
        background: "var(--color-info-soft)",
        color: "var(--color-info)",
        borderRadius: 8,
        fontSize: 11,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span style={{ fontWeight: 600 }}>{latestEvent}</span>
    </div>
  );
}

// ─── Express info line (carrier + tracking no) ─────────────────────────────────
function ExpressInfoLine({ expressNos }: { expressNos: string[] }) {
  const { t } = useTranslation("shipment");
  if (!expressNos || expressNos.length === 0) return null;
  return (
    <div
      style={{
        color: "var(--color-text-muted)",
        marginTop: 2,
        fontSize: 10,
        fontFamily: "var(--font-mono)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {t("card.express_label")}: {expressNos[0]}
      {expressNos.length > 1 ? ` +${expressNos.length - 1}` : ""}
    </div>
  );
}

// ─── Skeleton card ─────────────────────────────────────────────────────────────
function ShipmentCardSkeleton() {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--color-border)",
        borderRadius: 12,
        padding: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <TgSkeleton width={100} height={11} />
        <TgSkeleton width={70} height={18} radius={999} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <TgSkeleton height={13} width="80%" />
          <TgSkeleton height={10} width="55%" />
        </div>
        <TgSkeleton width={60} height={20} radius={4} />
      </div>
    </div>
  );
}

// ─── Load-more sentinel ────────────────────────────────────────────────────────
function LoadMoreSentinel({ onVisible }: { onVisible: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onVisible();
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [onVisible]);
  return <div ref={ref} style={{ height: 1 }} />;
}

// ─── Tab count helper ──────────────────────────────────────────────────────────
// UserStatistics only has waitConfirm + total. Others return null (no badge).
function tabCount(
  tab: ShipTab,
  stats:
    | { waitConfirmOrderQuantity?: number | null; expressOrderQuantity?: number | null }
    | undefined,
): number | null {
  if (!stats) return null;
  // expressOrderQuantity = total international shipment orders
  if (tab === "ALL") return stats.expressOrderQuantity ?? null;
  // waitConfirmOrderQuantity maps to WAIT_CONFIRM in shipment context
  if (tab === "WAIT_CONFIRM") return stats.waitConfirmOrderQuantity ?? null;
  return null;
}

// ─── Single shipment card ──────────────────────────────────────────────────────
function ShipmentCard({
  order,
  displayLocale,
  displayCurrency,
}: {
  order: OrderSummary;
  displayLocale: string;
  displayCurrency: string;
}) {
  const { t } = useTranslation("shipment");
  const status = order.status as string;
  const s = STATUS_STYLE[status] ?? DEFAULT_STYLE;
  const isShipped = status === "SHIPPED";
  const isWaitPicking = status === "WAIT_PICKING";
  const isWaitConfirm = status === "WAIT_CONFIRM";
  const isCompleted = status === "COMPLETED";

  // Collect all express nos from items
  const allExpressNos = Array.from(
    new Set(order.items.flatMap((it) => it.expressNoList ?? [])),
  );

  return (
    <Link
      to={"/order-detail/$orderNo" as never}
      params={{ orderNo: order.orderNo } as never}
      style={{
        background: "#fff",
        border: isShipped
          ? "1.5px solid var(--color-info)"
          : `1px solid ${s.borderColor}`,
        borderRadius: 12,
        padding: "12px 14px",
        textDecoration: "none",
        color: "inherit",
        display: "block",
        opacity: isCompleted ? 0.85 : 1,
      }}
    >
      {/* ── Header row: order no + status badge ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700 }}>
          № {order.orderNo}
        </span>
        <span
          style={{
            background: s.bg,
            color: s.fg,
            border: `1px solid ${s.dot}33`,
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: s.dot,
              display: "inline-block",
            }}
          />
          {order.statusName}
        </span>
      </div>

      {/* ── Body row: title / meta + price ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, fontSize: 11 }}>
        <div>
          <strong>{order.items[0]?.title ?? order.orderNo}</strong>
          <div style={{ color: "var(--color-text-muted)", marginTop: 2 }}>
            {t("card.items", { n: order.itemCount })}
            {/* deliver window for WAIT_CONFIRM */}
            {isWaitConfirm && order.orderTime && (
              <span style={{ marginLeft: 6 }}>· {t("card.deliver_window")}</span>
            )}
          </div>
          {/* Express tracking nos for SHIPPED */}
          {isShipped && <ExpressInfoLine expressNos={allExpressNos} />}
        </div>
        <div>
          {isCompleted ? (
            /* Completed CTA — no points badge per audit */
            <span
              style={{
                background: "var(--color-cta)",
                color: "#fff",
                padding: "5px 10px",
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 800,
                display: "inline-block",
                whiteSpace: "nowrap",
              }}
            >
              {t("card.review_cta")}
            </span>
          ) : (
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 14,
                fontWeight: 900,
                color: isShipped ? "var(--color-text)" : "var(--color-cta)",
                textAlign: "right",
              }}
            >
              {formatPrice(order.orderAmountRubKopecks, displayLocale as "ru-RU" | "zh-CN", (displayCurrency === "RUB" || displayCurrency === "CNY" ? displayCurrency : "RUB") as "RUB" | "CNY")}
            </div>
          )}
        </div>
      </div>

      {/* ── 5-step progress bar for WAIT_PICKING ── */}
      {isWaitPicking && <ShipmentProgressBar />}

      {/* ── Track summary for SHIPPED (no real-time data available from list API) ── */}
      {isShipped && allExpressNos.length > 0 && (
        <TrackSummaryBar latestEvent={null} />
      )}
    </Link>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export function ShipmentPage() {
  const { i18n, t } = useTranslation("shipment");
  const displayLocale = miniDisplayLocale(i18n.language);
  const displayCurrency = miniDisplayCurrency(i18n.language);
  const { token: authToken, status: authStatus } = useAuth();
  const [tab, setTab] = useState<ShipTab>("ALL");

  const buildInput = () => ({
    ...(tab !== "ALL" ? { status: tab } : {}),
    hideCancelOrder: 1 as const,
    pageSize: 10,
  });

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOrdersInfinite(authToken, buildInput());

  const { data: stats } = useUserStatistics(authToken);

  const orders = data?.orders ?? [];

  const onFetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ── booting ──
  if (authStatus === "booting") {
    return (
      <TgPage tabbar>
        <TgHeader title={t("head.title")} back={false} />
        <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: 10 }}>
          {[1, 2, 3].map((n) => (
            <ShipmentCardSkeleton key={n} />
          ))}
        </div>
        <TgBottomNav />
      </TgPage>
    );
  }

  // ── signed-out ──
  if (authStatus === "signed-out") {
    return (
      <TgPage tabbar>
        <TgHeader title={t("head.title")} back={false} />
        <TgEmpty
          icon="🚚"
          title={t("state.signin_t")}
          hint={t("state.signin_s")}
          cta={
            <Link
              to={"/profile" as never}
              style={{
                display: "inline-block",
                padding: "8px 18px",
                background: "var(--color-cta)",
                color: "#fff",
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              {t("state.signin_cta")}
            </Link>
          }
        />
        <TgBottomNav />
      </TgPage>
    );
  }

  return (
    <TgPage tabbar>
      <TgHeader title={t("head.title")} back={false} />

      {/* ── Status tabs with dynamic counts ── */}
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "8px 12px",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
          overflowX: "auto",
        }}
      >
        {TABS.map(({ key, i18nKey }) => {
          const isActive = tab === key;
          const count = tabCount(key, stats);
          const label = t(`tabs.${i18nKey}` as never);
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              style={{
                background: isActive ? "var(--color-text)" : "transparent",
                color: isActive ? "#fff" : "var(--color-text-muted)",
                padding: "6px 12px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                border: 0,
                whiteSpace: "nowrap",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              {label}
              {count != null && count > 0 ? ` ${count}` : ""}
            </button>
          );
        })}
      </div>

      <div
        className="tg-body"
        style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, padding: "10px 12px" }}
      >
        {/* ── Loading skeletons ── */}
        {isLoading && (
          <>
            {[1, 2, 3].map((n) => (
              <ShipmentCardSkeleton key={n} />
            ))}
          </>
        )}

        {/* ── Error state ── */}
        {isError && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 32 }}>⚠️</span>
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{t("state.error_t")}</div>
            <button
              type="button"
              onClick={() => refetch()}
              style={{
                padding: "7px 18px",
                background: "var(--color-cta)",
                color: "#fff",
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 12,
                border: 0,
              }}
            >
              {t("state.error_retry")}
            </button>
          </div>
        )}

        {/* ── Empty state ── */}
        {!isLoading && !isError && orders.length === 0 && (
          <TgEmpty icon="🚚" title={t("empty.title")} hint={t("empty.hint")} />
        )}

        {/* ── Order cards ── */}
        {orders.map((order) => (
          <ShipmentCard
            key={order.orderNo}
            order={order}
            displayLocale={displayLocale}
            displayCurrency={displayCurrency}
          />
        ))}

        {/* ── Load-more sentinel ── */}
        {!isLoading && orders.length > 0 && (
          <LoadMoreSentinel onVisible={onFetchMore} />
        )}

        {/* ── Fetching more indicator ── */}
        {isFetchingNextPage && (
          <div
            style={{
              padding: "8px 0",
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 11,
            }}
          >
            {t("state.loading")}
          </div>
        )}

        {/* ── All loaded indicator ── */}
        {!isLoading && !hasNextPage && orders.length > 0 && (
          <div
            style={{
              padding: "8px 0",
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 10,
            }}
          >
            · {orders.length} ·
          </div>
        )}
      </div>

      <TgBottomNav />
    </TgPage>
  );
}
